import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

//const API_BASE_URL = 'http://localhost:5000/api/accounts'; 
const API_BASE_URL = "https://unitedautomotors.store/api/accounts";
export default function ChartOfAccounts() {
    // --- Framework States ---
    const [isSaving, setIsSaving] = useState(false);
    const [heads, setHeads] = useState([]);
    const [subHeads, setSubHeads] = useState([]);
    const [subSubHeads, setSubSubHeads] = useState([]);
    const [accounts, setAccounts] = useState([]);

    // --- UI Expansion Controls ---
    const [expandedSubHeads, setExpandedSubHeads] = useState({});
    const [expandedSubSubHeads, setExpandedSubSubHeads] = useState({});

    // --- CASCADING FORM STATES ---
    const [isEditingL2, setIsEditingL2] = useState(false);
    const [l2Form, setL2Form] = useState({ SubHeadID: '', SubHeadName: '', ParentHeadID: '' });

    const [l3Form, setL3Form] = useState({
        selectedL1: '',
        selectedL2: '',
        SubSubHeadID: '',
        SubSubHeadName: ''
    });
    const [isEditingL3, setIsEditingL3] = useState(false);

    const [l4Form, setL4Form] = useState({
        id: '',
        selectedL1: '',
        selectedL2: '',
        SubSubHeadID: '',
        title: '',
        phone: '',
        address: '',
        reference_number: '',
        opening_balance: ''
    });

    // --- CORE API FLOWS ---
    const syncAllData = async () => {
        try {
            const [resL1, resL2, resL3, resL4] = await Promise.all([
                fetch(`${API_BASE_URL}/heads`).then(r => r.json()),
                fetch(`${API_BASE_URL}/subheads`).then(r => r.json()),
                fetch(`${API_BASE_URL}/subsubheads`).then(r => r.json()),
                fetch(`${API_BASE_URL}/accounts`).then(r => r.json())
            ]);

            if (resL1.success) setHeads(resL1.data);
            if (resL2.success) setSubHeads(resL2.data);
            if (resL3.success) setSubSubHeads(resL3.data);
            if (resL4.success) setAccounts(resL4.data);
        } catch (err) {
            console.error("Data syncing broke down: ", err);
        }
    };

    useEffect(() => { syncAllData(); }, []);

    // --- FORM SUBMIT HANDLERS ---
    const submitL2Form = async (e) => {
        e.preventDefault();
        if (!l2Form.SubHeadName || !l2Form.ParentHeadID) return alert("Please select L1 Head and write Sub-Head Title!");

        try {
            setIsSaving(true);
            const res = await fetch(`${API_BASE_URL}/subhead-upsert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(l2Form)
            });
            const data = await res.json();
            if (data.success) {
                clearL2State();
                syncAllData();
            }
        } catch (err) { console.error(err); }
        finally { setIsSaving(false); }
    };

    const clearL2State = () => {
        setL2Form({ SubHeadID: '', SubHeadName: '', ParentHeadID: '' });
        setIsEditingL2(false);
    };

    const submitL3Form = async (e) => {
        e.preventDefault();
        if (!l3Form.selectedL2 || !l3Form.SubSubHeadName.trim()) {
            return alert("Please select Sub-Head (Level 2) and enter Sub-Sub Head Title!");
        }

        try {
            setIsSaving(true);
            const res = await fetch(`${API_BASE_URL}/subsubhead-upsert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SubSubHeadID: l3Form.SubSubHeadID || null,
                    SubSubHeadName: l3Form.SubSubHeadName.trim(),
                    ParentSubHeadID: l3Form.selectedL2
                })
            });
            const data = await res.json();
            if (data.success) {
                clearL3State();
                syncAllData();
            }
        } catch (err) { console.error(err); }
        finally { setIsSaving(false); }
    };

    const clearL3State = () => {
        setL3Form({ selectedL1: '', selectedL2: '', SubSubHeadID: '', SubSubHeadName: '' });
        setIsEditingL3(false);
    };

    const submitL4Form = async (e) => {
        e.preventDefault();
        if (!l4Form.SubSubHeadID || !l4Form.title.trim()) {
            return alert("Please select Sub-Sub Head (Level 3) and enter Account Title!");
        }

        try {
            setIsSaving(true);
            const res = await fetch(`${API_BASE_URL}/account-upsert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: l4Form.id || null,
                    SubSubHeadID: l4Form.SubSubHeadID,
                    title: l4Form.title,
                    phone: l4Form.phone,
                    address: l4Form.address,
                    reference_number: l4Form.reference_number,
                    opening_balance: l4Form.opening_balance
                })
            });
            const data = await res.json();
            if (data.success) {
                clearL4State();
                syncAllData();
            }
        } catch (err) { console.error(err); }
        finally { setIsSaving(false); }
    };

    const clearL4State = () => {
        setL4Form({
            id: '',
            selectedL1: '',
            selectedL2: '',
            SubSubHeadID: '',
            title: '',
            phone: '',
            address: '',
            reference_number: '',
            opening_balance: ''
        });
    };

    // --- EXPORT PDF GENERATOR (FIXED PHONE NUMBERS INCLUDED) ---
  // --- EXPORT PDF GENERATOR (WITH HARDCODED "Phone: " PREFIX) ---
    const generatePDFReport = () => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginLeft = 15;
        const marginRight = 15;
        const contentWidth = pageWidth - marginLeft - marginRight;
        const col1Width = 30;  
        const col2Width = contentWidth - col1Width; 

        const colors = {
            primary: [30, 58, 95],      
            lightGray: [241, 245, 249],  
            mediumGray: [226, 232, 240], 
            darkGray: [148, 163, 184],   
            white: [255, 255, 255],
            textDark: [30, 41, 59],      
            textMedium: [71, 85, 105],   
            textLight: [100, 116, 139]  
        };

        doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        doc.rect(0, 0, pageWidth, 22, 'F');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(255, 255, 255);
        doc.text("Chart of Accounts", marginLeft, 14);

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(220, 220, 220);
        doc.text(`United Motors ERP — Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth - marginRight, 14, { align: 'right' });

        let yPos = 28;
        doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
        doc.rect(marginLeft, yPos - 4, contentWidth, 8, 'F');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
        doc.text("CODE", marginLeft + 2, yPos + 1);
        doc.text("TITLE OF ACCOUNT & DETAILS", marginLeft + col1Width + 2, yPos + 1);

        yPos += 5;
        doc.setDrawColor(colors.mediumGray[0], colors.mediumGray[1], colors.mediumGray[2]);
        doc.setLineWidth(0.5);
        doc.line(marginLeft, yPos, marginLeft + contentWidth, yPos);
        yPos += 6;

        const checkPageOverflow = (neededSpace = 10) => {
            if (yPos + neededSpace > 280) {
                doc.addPage();
                yPos = 15;
                doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
                doc.rect(marginLeft, yPos - 4, contentWidth, 8, 'F');
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
                doc.text("CODE", marginLeft + 2, yPos + 1);
                doc.text("TITLE OF ACCOUNT & DETAILS", marginLeft + col1Width + 2, yPos + 1);
                yPos += 5;
                doc.setDrawColor(colors.mediumGray[0], colors.mediumGray[1], colors.mediumGray[2]);
                doc.line(marginLeft, yPos, marginLeft + contentWidth, yPos);
                yPos += 6;
            }
        };

        const drawRow = (code, title, level, phone = '') => {
            let indentX = 0;
            let fontSize = 10;
            let fontStyle = "normal";
            let textColor = colors.textDark;
            let bgColor = null;
            let codeColor = colors.textMedium;
            let rowHeight = 7;

            if (level === 0) {
                fontStyle = "bold";
                fontSize = 12;
                textColor = colors.white;
                codeColor = [230, 230, 230];
                bgColor = colors.primary;
                rowHeight = 9;
            } else if (level === 1) {
                fontStyle = "bold";
                fontSize = 10.5;
                textColor = colors.primary;
                codeColor = colors.primary;
                bgColor = [248, 250, 252];
            } else if (level === 2) {
                fontStyle = "bold";
                fontSize = 10;
                textColor = colors.textDark;
                codeColor = colors.textMedium;
                indentX = 5;
            } else if (level === 3 || level === 4) {
                fontStyle = "normal";
                fontSize = 9.5;
                textColor = colors.textMedium;
                codeColor = colors.textLight;
                indentX = 10;
            }

            // --- Yahan Title ke sath "Phone: [number]" automatically format kiya gaya hai ---
            let fullDisplayTitle = title;
            if (phone && phone.trim() !== '' && phone !== 'null') {
                fullDisplayTitle += ` | Phone: ${phone}`;
            }

            const maxTitleWidth = col2Width - indentX - 6;
            doc.setFont(fontStyle);
            doc.setFontSize(fontSize);
            const splitTitle = doc.splitTextToSize(fullDisplayTitle, maxTitleWidth);
            const lineHeight = fontSize * 0.42;
            const textHeight = splitTitle.length * lineHeight;
            const neededSpace = Math.max(textHeight + 3, rowHeight);

            checkPageOverflow(neededSpace + 2);

            if (bgColor) {
                doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
                doc.rect(marginLeft, yPos - 3.5, contentWidth, neededSpace + 1, 'F');
            }

            doc.setFont("helvetica", level === 0 ? "bold" : fontStyle);
            doc.setFontSize(fontSize);
            doc.setTextColor(codeColor[0], codeColor[1], codeColor[2]);
            doc.text(code.toString(), marginLeft + 2, yPos);

            const titleX = marginLeft + col1Width + 2 + indentX;
            doc.setFont("helvetica", fontStyle);
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.text(splitTitle, titleX, yPos);

            yPos += neededSpace + 1;

            if (level !== 0) {
                doc.setDrawColor(241, 245, 249);
                doc.setLineWidth(0.2);
                doc.line(marginLeft + indentX, yPos - 0.5, marginLeft + contentWidth, yPos - 0.5);
            }
        };

        heads.forEach(h => {
            drawRow(h.HeadID, h.HeadName, 0);
            
            subHeads.filter(sh => sh.ParentHeadID == h.HeadID).forEach(sh => {
                drawRow(sh.SubHeadID, sh.SubHeadName, 1);

                subSubHeads.filter(ssh => ssh.ParentSubHeadID == sh.SubHeadID).forEach(ssh => {
                    drawRow(ssh.SubSubHeadID, ssh.SubSubHeadName, 2);

                    accounts.filter(acc => acc.SubSubHeadID == ssh.SubSubHeadID).forEach(acc => {
                        drawRow(acc.id, acc.title, 3, acc.phone);
                    });
                });
            });
        });

        yPos += 4;
        doc.setDrawColor(colors.mediumGray[0], colors.mediumGray[1], colors.mediumGray[2]);
        doc.setLineWidth(0.5);
        doc.line(marginLeft, yPos, marginLeft + contentWidth, yPos);

        yPos += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(colors.darkGray[0], colors.darkGray[1], colors.darkGray[2]);
        doc.text("United Motors ERP System — Chart of Accounts Report", pageWidth / 2, yPos, { align: 'center' });

        doc.save(`Chart_Of_Accounts_${new Date().toISOString().slice(0,10)}.pdf`);
    };

    return (
        <div style={{ padding: '30px 40px', fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', background: '#f8fafc', minHeight: '100vh', position: 'relative' }}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            
            {isSaving && (
                <div style={styles.loaderOverlay}>
                    <div style={styles.loaderContainer}>
                        <div style={styles.spinner}></div>
                        <p style={styles.loaderText}>Saving your data...</p>
                        <p style={styles.loaderSubText}>Please wait while we process your request</p>
                    </div>
                </div>
            )}

            {/* BRAND HEADER & PDF BUTTON */}
            <div style={{ marginBottom: '30px', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ margin: 0, color: '#0f172a', fontSize: '26px', fontWeight: '700' }}>Chart of Accounts Dashboard</h1>
                    <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '14px' }}>Manage corporate hierarchy nodes and customer/vendor ledger accounts.</p>
                </div>
                <button onClick={generatePDFReport} style={styles.exportPdfButton}>🖨️ Export Full Account Tree PDF</button>
            </div>

            {/* --- THREE TIERS FORM CONTROLLERS GRID --- */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
                {/* 1. LEVEL 3 ADDER */}
                <div style={styles.card}>
                    <h3 style={styles.cardHeader}>{isEditingL3 ? '📝 Edit Sub-Sub Head Category (Level 3)' : '⚡ Create Sub-Sub Head Category (Level 3)'}</h3>
                    <form onSubmit={submitL3Form} style={styles.verticalForm}>
                        <div>
                            <label style={styles.fieldLabel}>1. Select Main Head (L1)</label>
                            <select style={styles.textInput} value={l3Form.selectedL1} onChange={e => setL3Form({ ...l3Form, selectedL1: e.target.value, selectedL2: '' })}>
                                <option value="">-- Select L1 Head --</option>
                                {heads.map(h => <option key={h.HeadID} value={h.HeadID}>{h.HeadName}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>2. Select Sub-Head (L2)</label>
                            <select style={styles.textInput} value={l3Form.selectedL2} disabled={!l3Form.selectedL1} onChange={e => setL3Form({ ...l3Form, selectedL2: e.target.value })}>
                                <option value="">-- Select L2 Sub-Head --</option>
                                {subHeads.filter(sh => sh.ParentHeadID == l3Form.selectedL1).map(sh => (
                                    <option key={sh.SubHeadID} value={sh.SubHeadID}>{sh.SubHeadName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={styles.fieldLabel}>3. Sub-Sub Head Name (L3)</label>
                            <input type="text" style={styles.textInput} placeholder="e.g., Local Banks" value={l3Form.SubSubHeadName} onChange={e => setL3Form({ ...l3Form, SubSubHeadName: e.target.value })} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="submit" style={isEditingL3 ? styles.warningButton : styles.successButton} disabled={!l3Form.selectedL2 || isSaving}>
                                {isEditingL3 ? 'Update L3 Node' : 'Deploy L3 Node'}
                            </button>
                            {isEditingL3 && <button type="button" onClick={clearL3State} style={styles.cancelButton}>Cancel</button>}
                        </div>
                    </form>
                </div>

                {/* 2. LEVEL 4 ACCOUNT OPENER / EDITOR */}
                <div style={styles.card}>
                    <h3 style={styles.cardHeader}>{l4Form.id ? '📝 Edit Ledger Account (Level 4)' : '➕ Open New Ledger Account (Level 4)'}</h3>
                    <form onSubmit={submitL4Form} style={styles.verticalForm}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={styles.fieldLabel}>Select L1 Head</label>
                                <select style={styles.textInput} value={l4Form.selectedL1} onChange={e => setL4Form({ ...l4Form, selectedL1: e.target.value, selectedL2: '', SubSubHeadID: '' })}>
                                    <option value="">-- L1 Head --</option>
                                    {heads.map(h => <option key={h.HeadID} value={h.HeadID}>{h.HeadName}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>Select L2 Sub-Head</label>
                                <select style={styles.textInput} disabled={!l4Form.selectedL1} value={l4Form.selectedL2} onChange={e => setL4Form({ ...l4Form, selectedL2: e.target.value, SubSubHeadID: '' })}>
                                    <option value="">-- L2 Sub-Head --</option>
                                    {subHeads.filter(sh => sh.ParentHeadID == l4Form.selectedL1).map(sh => (
                                        <option key={sh.SubHeadID} value={sh.SubHeadID}>{sh.SubHeadName}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>Select L3 Sub-Sub</label>
                                <select style={styles.textInput} disabled={!l4Form.selectedL2} value={l4Form.SubSubHeadID} onChange={e => setL4Form({ ...l4Form, SubSubHeadID: e.target.value })}>
                                    <option value="">-- L3 Sub-Sub --</option>
                                    {subSubHeads.filter(ssh => ssh.ParentSubHeadID == l4Form.selectedL2).map(ssh => (
                                        <option key={ssh.SubSubHeadID} value={ssh.SubSubHeadID}>{ssh.SubSubHeadName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={styles.fieldLabel}>Account Title</label>
                                <input type="text" placeholder="e.g., Meezan Current A/C" style={styles.textInput} value={l4Form.title} onChange={e => setL4Form({ ...l4Form, title: e.target.value })} />
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>Opening Balance (Rs.)</label>
                                <input type="number" placeholder="0.00" style={styles.textInput} value={l4Form.opening_balance} onChange={e => setL4Form({ ...l4Form, opening_balance: e.target.value })} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={styles.fieldLabel}>Phone Link</label>
                                <input type="text" placeholder="03xx-xxxxxxx" style={styles.textInput} value={l4Form.phone} onChange={e => setL4Form({ ...l4Form, phone: e.target.value })} />
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>Reference No.</label>
                                <input type="text" placeholder="REF-XYZ" style={styles.textInput} value={l4Form.reference_number} onChange={e => setL4Form({ ...l4Form, reference_number: e.target.value })} />
                            </div>
                            <div>
                                <label style={styles.fieldLabel}>Address Space</label>
                                <input type="text" placeholder="Office Location" style={styles.textInput} value={l4Form.address} onChange={e => setL4Form({ ...l4Form, address: e.target.value })} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="submit" style={l4Form.id ? styles.warningButton : styles.primaryButton} disabled={!l4Form.SubSubHeadID || isSaving}>
                                {l4Form.id ? 'Save Ledger Updates' : 'Deploy Ledger Account'}
                            </button>
                            {l4Form.id && <button type="button" onClick={clearL4State} style={styles.cancelButton}>Cancel</button>}
                        </div>
                    </form>
                </div>
            </div>

            {/* --- L2 SUB-HEAD ADDER PANEL --- */}
            <div style={styles.card}>
                <h3 style={styles.cardHeader}>{isEditingL2 ? '📝 Update Sub-Category Head (Level 2)' : '✨ Create Sub-Category Head (Level 2)'}</h3>
                <form onSubmit={submitL2Form} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 2 }}>
                        <label style={styles.fieldLabel}>Link Control Head (Level 1)</label>
                        <select style={styles.textInput} value={l2Form.ParentHeadID} onChange={e => setL2Form({ ...l2Form, ParentHeadID: e.target.value })}>
                            <option value="">-- Select Level 1 Account Head --</option>
                            {heads.map(h => <option key={h.HeadID} value={h.HeadID}>{h.HeadName}</option>)}
                        </select>
                    </div>
                    <div style={{ flex: 2 }}>
                        <label style={styles.fieldLabel}>Sub-Head Title (L2)</label>
                        <input type="text" style={styles.textInput} placeholder="e.g., Current Assets" value={l2Form.SubHeadName} onChange={e => setL2Form({ ...l2Form, SubHeadName: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="submit" style={isEditingL2 ? styles.warningButton : styles.primaryButton} disabled={isSaving}>
                            {isEditingL2 ? 'Apply Updates' : 'Add Sub-Head'}
                        </button>
                        {isEditingL2 && <button type="button" onClick={clearL2State} style={styles.cancelButton}>Cancel</button>}
                    </div>
                </form>
            </div>

            {/* --- COA MASTER TREE VIEWER --- */}
            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden', marginTop: '30px' }}>
                <div style={{ background: '#0f172a', color: '#fff', padding: '16px 24px', fontWeight: '600', fontSize: '15px' }}>
                    STRUCTURE & REGISTRY HIERARCHY TREE
                </div>

                <div style={{ padding: '20px' }}>
                    {subHeads.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', border: '2px dashed #e2e8f0', borderRadius: '8px' }}>
                            No structures found.
                        </div>
                    ) : subHeads.map(sh => {
                        const isExpandedL2 = !!expandedSubHeads[sh.SubHeadID];
                        const nestedL3List = subSubHeads.filter(ssh => ssh.ParentSubHeadID === sh.SubHeadID);

                        return (
                            <div key={sh.SubHeadID} style={{ marginBottom: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px 20px', borderBottom: isExpandedL2 ? '1px solid #e2e8f0' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }} onClick={() => setExpandedSubHeads(p => ({ ...p, [sh.SubHeadID]: !p[sh.SubHeadID] }))}>
                                        <span style={{ fontSize: '18px' }}>{isExpandedL2 ? '📂' : '📁'}</span>
                                        <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '15px' }}>{sh.SubHeadName}</span>
                                        <span style={styles.idBadge}>L2 ID: {sh.SubHeadID}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={styles.actionInlineEdit} onClick={() => {
                                            setIsEditingL2(true);
                                            setL2Form({ SubHeadID: sh.SubHeadID, SubHeadName: sh.SubHeadName, ParentHeadID: sh.ParentHeadID });
                                            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                                        }}>Modify</button>
                                        <button style={isExpandedL2 ? styles.actionToggleBtnActive : styles.actionToggleBtn} onClick={() => setExpandedSubHeads(p => ({ ...p, [sh.SubHeadID]: !p[sh.SubHeadID] }))}>
                                            {isExpandedL2 ? 'Collapse' : 'Expand Structure'}
                                        </button>
                                    </div>
                                </div>

                                {isExpandedL2 && (
                                    <div style={{ padding: '20px', background: '#fff' }}>
                                        {nestedL3List.length === 0 ? (
                                            <div style={styles.dimmedMeta}>No sub-sub categories defined.</div>
                                        ) : nestedL3List.map(ssh => {
                                            const isExpandedL3 = !!expandedSubSubHeads[ssh.SubSubHeadID];
                                            const targetedL4Accounts = accounts.filter(acc => acc.SubSubHeadID === ssh.SubSubHeadID);

                                            return (
                                                <div key={ssh.SubSubHeadID} style={{ marginBottom: '12px', border: '1px solid #f1f5f9', borderRadius: '8px', overflow: 'hidden' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#f1f5f9' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }} onClick={() => setExpandedSubSubHeads(p => ({ ...p, [ssh.SubSubHeadID]: !p[ssh.SubSubHeadID] }))}>
                                                            <span>{isExpandedL3 ? '📖' : '📘'}</span>
                                                            <span style={{ fontWeight: '600', color: '#334155', fontSize: '14px' }}>{ssh.SubSubHeadName}</span>
                                                            <span style={styles.miniIdCode}>L3 ID: {ssh.SubSubHeadID}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button style={styles.actionInlineEditMini} onClick={() => {
                                                                setIsEditingL3(true);
                                                                setL3Form({
                                                                    selectedL1: sh.ParentHeadID,
                                                                    selectedL2: sh.SubHeadID,
                                                                    SubSubHeadID: ssh.SubSubHeadID,
                                                                    SubSubHeadName: ssh.SubSubHeadName
                                                                });
                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                            }}>Rename / Edit</button>
                                                            <button style={isExpandedL3 ? styles.actionToggleBtnActiveMini : styles.actionToggleBtnMini} onClick={() => setExpandedSubSubHeads(p => ({ ...p, [ssh.SubSubHeadID]: !p[ssh.SubSubHeadID] }))}>
                                                                {isExpandedL3 ? 'Close Registers' : `Ledgers (${targetedL4Accounts.length})`}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* LEVEL 4 ACCOUNTS REGISTER AREA WITH WORKING MODIFY BUTTON */}
                                                    {isExpandedL3 && (
                                                        <div style={{ padding: '16px', background: '#fff', borderTop: '1px solid #f1f5f9' }}>
                                                            {targetedL4Accounts.length === 0 ? (
                                                                <div style={styles.dimmedMeta}>No active ledger profiles mapped.</div>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {targetedL4Accounts.map(acc => (
                                                                        <div key={acc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                                <span style={{ color: '#2563eb', fontWeight: '700', fontFamily: 'monospace', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>ID: {acc.id}</span>
                                                                                <strong style={{ color: '#1e293b', fontSize: '14px' }}>{acc.title}</strong>
                                                                                {acc.reference_number && <span style={styles.metaLabel}>Ref: {acc.reference_number}</span>}
                                                                                {acc.phone && <span style={styles.metaLabel}>📞 {acc.phone}</span>}
                                                                                <span style={styles.balLabel}>Opening Balance: Rs. {parseFloat(acc.opening_balance || 0).toLocaleString()}</span>
                                                                            </div>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                                {/* ✅ FIXED MODIFY ACCOUNT BUTTON */}
                                                                                <button style={styles.actionInlineEditMini} onClick={() => {
                                                                                    setL4Form({
                                                                                        id: acc.id,
                                                                                        selectedL1: sh.ParentHeadID,
                                                                                        selectedL2: sh.SubHeadID,
                                                                                        SubSubHeadID: ssh.SubSubHeadID,
                                                                                        title: acc.title,
                                                                                        phone: acc.phone || '',
                                                                                        address: acc.address || '',
                                                                                        reference_number: acc.reference_number || '',
                                                                                        opening_balance: acc.opening_balance || ''
                                                                                    });
                                                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                                }}>Modify Account</button>
                                                                                <span style={{ ...styles.statusTag, background: acc.status === 'active' || acc.status === '1' ? '#dcfce7' : '#fee2e2', color: acc.status === 'active' || acc.status === '1' ? '#15803d' : '#b91c1c' }}>{acc.status === '1' || acc.status === 'active' ? 'Active' : 'Inactive'}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// --- DESIGN SYSTEM PALETTE ---
const styles = {
    card: { background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
    cardHeader: { margin: '0 0 18px 0', color: '#1e293b', fontSize: '15px', fontWeight: '600', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px' },
    verticalForm: { display: 'flex', flexDirection: 'column', gap: '14px' },
    fieldLabel: { fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' },
    textInput: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#1e293b', background: '#fff' },
    primaryButton: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    successButton: { background: '#0f766e', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    warningButton: { background: '#d97706', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    cancelButton: { background: '#64748b', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    exportPdfButton: { background: '#ef4444', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', boxShadow: '0 4px 6px rgba(239, 68, 68, 0.2)' },
    actionToggleBtn: { background: '#fff', color: '#2563eb', border: '1px solid #2563eb', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    actionToggleBtnActive: { background: '#2563eb', color: '#fff', border: '1px solid #2563eb', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    actionToggleBtnMini: { background: '#fff', color: '#4f46e5', border: '1px solid #c7d2fe', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    actionToggleBtnActiveMini: { background: '#4f46e5', color: '#fff', border: '1px solid #4f46e5', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    actionInlineEdit: { background: '#fef3c7', color: '#b45309', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    actionInlineEditMini: { background: '#fff', color: '#b45309', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' },
    idBadge: { background: '#e2e8f0', color: '#475569', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: '600', fontFamily: 'monospace' },
    miniIdCode: { background: '#e2e8f0', color: '#475569', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' },
    dimmedMeta: { color: '#94a3b8', fontSize: '13px', padding: '10px 0', textAlign: 'center' },
    metaLabel: { background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', color: '#475569', fontSize: '11px' },
    balLabel: { background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', color: '#b45309', fontSize: '11px', fontWeight: '600' },
    statusTag: { padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' },
    loaderOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' },
    loaderContainer: { backgroundColor: '#fff', padding: '40px 60px', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
    spinner: { width: '50px', height: '50px', border: '4px solid #e2e8f0', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1.5s linear infinite' },
    loaderText: { margin: 0, color: '#0f172a', fontSize: '18px', fontWeight: '700' },
    loaderSubText: { margin: 0, color: '#64748b', fontSize: '13px' }
};